import type { ReactNode } from 'react';

export type RiskTier = 'green' | 'orange' | 'red';

interface RecommendationsCardProps {
  tier: RiskTier;
  title: string;
  icon: ReactNode;
  bullets: string[];
}

const TIER_STYLES: Record<RiskTier, string> = {
  green: 'bg-green-50 border-green-200',
  orange: 'bg-orange-50 border-orange-200',
  red: 'bg-red-50 border-red-200',
};

export function RecommendationsCard({ tier, title, icon, bullets }: RecommendationsCardProps) {
  return (
    <div
      className={`rounded-xl border px-4 py-4 ${TIER_STYLES[tier]}`}
      role="region"
      aria-label="Sağlık önerileri"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl shrink-0" aria-hidden>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="mb-3 text-lg font-bold text-[#1A1A1A]">{title}</h3>
          <ul className="list-inside list-disc space-y-2 text-sm leading-relaxed text-[#1A1A1A]">
            {bullets.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const GREEN_CONTENT = {
  title: 'Harika Gidiyorsunuz!',
  icon: '✅',
  bullets: [
    'Mevcut yaşam tarzınız kalp sağlığınızı koruyor. Bunu sürdürmek için:',
    'Günde en az 30 dakika tempolu yürüyüş yapın.',
    'Akdeniz tipi beslenmeye (sebze, zeytinyağı) devam edin.',
    'Tansiyonunuzu yılda bir kez kontrol ettirin.',
  ],
};

const ORANGE_CONTENT = {
  title: 'Dikkat, Önlem Alma Zamanı!',
  icon: '⚠️',
  bullets: [
    'Risk seviyeniz yükselmeye başlamış. Küçük değişikliklerle bunu tersine çevirebilirsiniz:',
    'En kısa sürede bir hekime danışın.',
    'Sigara kullanıyorsanız bırakmayı hedefleyin.',
    'Tuzu azaltın ve haftada en az 150 dakika hareket edin.',
  ],
};

const RED_CONTENT = {
  title: 'Önemli Uyarı! Eylem Gerekiyor',
  icon: '🚨',
  bullets: [
    'Kalp sağlığınız risk altında olabilir. Lütfen ihmal etmeyin:',
    'Vakit kaybetmeden bir Kardiyoloji uzmanından randevu alın.',
    'Tansiyon ve kolesterol değerlerinizi sıkı takip edin.',
    'Doktorunuz ilaç önerdiyse düzenli kullanın.',
  ],
};

export function getRecommendationsByCategory(category: 'low' | 'moderate' | 'high' | 'veryHigh') {
  switch (category) {
    case 'low':
    case 'moderate':
      return { tier: 'green' as RiskTier, ...GREEN_CONTENT };
    case 'high':
      return { tier: 'orange' as RiskTier, ...ORANGE_CONTENT };
    case 'veryHigh':
      return { tier: 'red' as RiskTier, ...RED_CONTENT };
  }
}
